import React from 'react';
import { useTranslation } from 'react-i18next';
import { OrderV2 } from '@kotipizzagroup/kotipizza-orders-api-client';
import { StyledPaper, TitleRow } from '../../Styled/Styled';
import { CustomerDetailsContainer, InformationRow, InformationText } from './Styled';
import { isDevEnvironment } from '../../utils/configuration';
import { formatEuro } from '../../utils/formatters';
import { ORDERS_READ, usePermission } from '../../utils/permissions';
import styled from 'styled-components';

// Module-level: usePermission memoises on the array's identity, so a literal here would rebuild the
// check on every render.
const REQUIRED_PERMISSIONS = [ORDERS_READ];

// A full URL is one unbreakable token, so it needs to be allowed to break mid-word.
const TrackingLink = styled.a`
  overflow-wrap: anywhere;
`;

const PaymentRow = styled.div`
  display: flex;
  flex-direction: row;
  padding: 0 0 0;
`;

/**
 * The internal record behind an order — the ids, the recording, the payment references. A card like
 * its siblings rather than the collapsible bar it used to be, and only for users who may read
 * orders in their own right.
 */
export const OrderAdditionalDetails: React.FC<{ order: OrderV2 }> = ({ order }) => {
  const { t } = useTranslation();
  const canViewOtherDetails = usePermission(REQUIRED_PERMISSIONS);

  if (!canViewOtherDetails) {
    return null;
  }

  return (
    <StyledPaper variant="outlined">
      <CustomerDetailsContainer>
        <TitleRow>
          <h3>{t('Other details')}</h3>
        </TitleRow>
        <InformationRow>
          <b>{t('S4D order id')}:</b>&nbsp;{order.s4dOrderId ?? '-'}
        </InformationRow>
        <InformationRow>
          <b>{t('S4D internal order id')}:</b>&nbsp;{order.s4dInternalOrderId ?? '-'}
        </InformationRow>
        {order.id && (
          <InformationRow>
            <b>{t('Clarity recording')}:</b>&nbsp;
            <a
              href={`https://clarity.microsoft.com/projects/view/d8px8tsusp/impressions?Variables=orderId%3A${order.id}&date=Last%2030%20days&smartEvents=Checkout`}
              target="_blank"
            >
              {t('global.search')}
            </a>
          </InformationRow>
        )}
        {order.id && order.orderKey && (
          <InformationRow>
            <b>{t('Order tracking')}:</b>&nbsp;
            <TrackingLink
              href={`https://kotipizza${isDevEnvironment() ? 'dev' : ''}.fi/tilaus/${order.id}?orderKey=${
                order.orderKey
              }`}
              target="_blank"
            >{`https://www.kotipizza${isDevEnvironment() ? 'dev' : ''}.fi/tilaus/${order.id}`}</TrackingLink>
          </InformationRow>
        )}
        <InformationText>
          {Array.isArray(order.shoppingCartPrice?.paymentRows) && order.shoppingCartPrice.paymentRows.length > 0
            ? order.shoppingCartPrice.paymentRows.map((row, idx) => (
                <React.Fragment key={idx}>
                  <PaymentRow key={`method-${idx}`}>
                    <b>
                      {t('Payment method')} {idx + 1}: {row.paymentMethod}
                    </b>
                  </PaymentRow>
                  <PaymentRow key={`amount-${idx}`}>
                    {t('Amount')}: {formatEuro(row.amount)}
                  </PaymentRow>
                  <PaymentRow key={`reference-${idx}`} style={{ paddingBottom: '10px' }}>
                    {t('Payment reference')}: {row.paymentReference}
                  </PaymentRow>
                </React.Fragment>
              ))
            : null}
        </InformationText>
        {order.orderPaymentCancellationTimestamp && (
          <>
            <InformationRow>
              <strong>{t('Payment cancellation details')}</strong>
            </InformationRow>
            <InformationText>
              <ul>
                {order.orderPaymentCancellationReason && <li>{order.orderPaymentCancellationReason}</li>}
                {order.orderPaymentCancellationDetailedReason && (
                  <li>{order.orderPaymentCancellationDetailedReason}</li>
                )}
              </ul>
            </InformationText>
          </>
        )}
      </CustomerDetailsContainer>
    </StyledPaper>
  );
};
